export interface POAExtracted {
  importer_name: string
  importer_address: string | null
  eori_number: string | null
  vat_number: string | null
  signatory_name: string | null
  signed_date: string | null
  valid_until: string | null
  is_open_ended: boolean
  is_uk_local_company: boolean
  is_pva: boolean
  is_cds_approved: boolean
  confidence: number
}

export interface ValidationWarning {
  field: string
  message: string
}

export async function parsePOAWithClaude(contentInput: string | { base64?: string; text?: string; mimeType: string }, agentName: string): Promise<POAExtracted> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL')
  if (!anonKey) throw new Error('Missing VITE_SUPABASE_ANON_KEY')

  let body: Record<string, any>
  if (typeof contentInput === 'string') {
    body = { base64: contentInput, mimeType: 'application/pdf', agentName }
  } else {
    body = { ...contentInput, agentName }
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/parse-poa`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${anonKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const errText = await response.text()
    let errorDetail = errText
    try {
      const errJson = JSON.parse(errText)
      errorDetail = errJson.error || errText
    } catch {
      // errText is not JSON, use as-is
    }
    throw new Error(`Parse error: ${response.statusText} - ${errorDetail}`)
  }

  return await response.json() as POAExtracted
}

export function validateExtractedPOA(extracted: POAExtracted): ValidationWarning[] {
  const warnings: ValidationWarning[] = []
  const today = new Date()

  if (!extracted.eori_number) {
    warnings.push({ field: 'eori_number', message: 'EORI number is missing' })
  } else if (!/^GB\d{10,15}$/i.test(extracted.eori_number)) {
    warnings.push({
      field: 'eori_number',
      message: `EORI format invalid. Expected GB + 10-15 digits, got ${extracted.eori_number}`,
    })
  }

  if (!extracted.signed_date) {
    warnings.push({ field: 'signed_date', message: 'Signed date is missing' })
  } else {
    const signedDate = new Date(extracted.signed_date)
    if (signedDate > today) {
      warnings.push({ field: 'signed_date', message: `Signed date is in the future: ${extracted.signed_date}` })
    }
  }

  if (!extracted.importer_name || extracted.importer_name.length < 3) {
    warnings.push({
      field: 'importer_name',
      message: `Importer name is too short or invalid: "${extracted.importer_name}"`,
    })
  } else if (/^[^a-z]{0,3}$/i.test(extracted.importer_name.trim())) {
    warnings.push({
      field: 'importer_name',
      message: `Importer name looks like garbage: "${extracted.importer_name}"`,
    })
  }

  return warnings
}
